import React, { useState } from 'react';
import { Shield, Cookie, FileText, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const [showModal, setShowModal] = useState(null);

  // Schema.org pour le footer
  React.useEffect(() => {
    const footerSchema = {
      "@context": "https://schema.org",
      "@type": "WPFooter",
      "copyrightYear": new Date().getFullYear(),
      "copyrightHolder": {
        "@type": "Organization",
        "name": "EvoCore PACA",
        "url": "https://www.evocore-paca.fr"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(footerSchema);
    script.id = 'footer-schema';
    
    const existing = document.getElementById('footer-schema');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('footer-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const openModal = (type) => {
    setShowModal(type);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      <footer className="bg-slate-900/95 border-t border-slate-700/50 mt-12" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">
                EvoCore PACA
              </h3>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                <strong className="text-white">Votre partenaire IA en région Sud.</strong>
                <br />
                Nous accompagnons les auto-entrepreneurs et PME de la Côte d'Azur dans leur transformation numérique 
                grâce à l'intelligence artificielle.
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                  <div>
                    <strong className="text-white block">Région PACA</strong>
                    <span className="text-xs">Provence-Alpes-Côte d'Azur, France</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0 text-blue-400" />
                  <a 
                    href="mailto:contact@evocore-paca.fr" 
                    className="hover:text-blue-400 transition-colors hover:underline"
                  >
                    contact@evocore-paca.fr
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 flex-shrink-0 text-blue-400" />
                  <a 
                    href="tel:+33XXXXXXXXX" 
                    className="hover:text-blue-400 transition-colors"
                  >
                    +33 X XX XX XX XX
                  </a>
                </div>
              </div>
              
              {/* Badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  🇫🇷 French Tech
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-400/30">
                  🤖 IA & Innovation
                </span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Navigation</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a 
                    href="#projet" 
                    className="hover:text-blue-400 transition-colors flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('[data-testid="project-info"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span className="text-blue-400">→</span>
                    Le Projet EvoCore PACA
                  </a>
                </li>
                <li>
                  <a 
                    href="#equipe" 
                    className="hover:text-blue-400 transition-colors flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('[data-testid="team-partners"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span className="text-blue-400">→</span>
                    Notre Équipe & Partenaires
                  </a>
                </li>
                <li>
                  <a 
                    href="#financier" 
                    className="hover:text-blue-400 transition-colors flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('[data-testid="financial-dashboard"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span className="text-blue-400">→</span>
                    Données Financières
                  </a>
                </li>
                <li>
                  <a 
                    href="#documents" 
                    className="hover:text-blue-400 transition-colors flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('[data-testid="document-repository"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span className="text-blue-400">→</span>
                    Documents & Ressources
                  </a>
                </li>
                <li>
                  <a 
                    href="#contact" 
                    className="hover:text-blue-400 transition-colors flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('[data-testid="investor-inquiry"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span className="text-blue-400">→</span>
                    Nous Contacter
                  </a>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Nos Services</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Intelligence Artificielle pour auto-entrepreneurs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Transformation numérique PME</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Solutions IA innovantes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Accompagnement région PACA</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">✓</span>
                  <span>Conseil et formation IA</span>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Informations Légales</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button
                    onClick={() => openModal('mentions')}
                    className="hover:text-blue-400 transition-colors text-left"
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Mentions Légales
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => openModal('privacy')}
                    className="hover:text-blue-400 transition-colors text-left"
                  >
                    <Shield className="w-4 h-4 inline mr-2" />
                    Politique de Confidentialité
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => openModal('cookies')}
                    className="hover:text-blue-400 transition-colors text-left"
                  >
                    <Cookie className="w-4 h-4 inline mr-2" />
                    Politique des Cookies
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => openModal('cgu')}
                    className="hover:text-blue-400 transition-colors text-left"
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    CGU / CGV
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* SEO Keywords Section */}
          <div className="border-t border-slate-700/50 pt-6 pb-4">
            <div className="text-center mb-4">
              <h4 className="text-white font-semibold text-sm mb-3">
                EvoCore PACA - Intelligence Artificielle pour Auto-Entrepreneurs
              </h4>
              <p className="text-gray-500 text-xs max-w-4xl mx-auto leading-relaxed">
                <strong className="text-gray-400">Mots-clés :</strong> Intelligence artificielle PACA, IA auto-entrepreneurs, 
                transformation numérique Côte d'Azur, solutions IA Nice, French Tech région Sud, 
                innovation PME PACA, accompagnement digital auto-entrepreneurs, plateforme IA Provence-Alpes-Côte d'Azur, 
                startup IA Nice Cannes Monaco, conseil IA entreprises PACA
              </p>
            </div>
            
            {/* Social Links */}
            <div className="flex justify-center gap-4 mb-4">
              <a 
                href="https://www.linkedin.com/company/evocore-paca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="https://twitter.com/evocorepaca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
                aria-label="Twitter"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/evocorepaca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="mailto:contact@evocore-paca.fr" 
                className="text-gray-400 hover:text-blue-400 transition-colors"
                aria-label="Email"
              >
                <Mail className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-700/50 pt-6 text-center">
            <p className="text-gray-400 text-sm mb-2">
              &copy; {new Date().getFullYear()} EvoCore PACA. Tous droits réservés.
            </p>
            <p className="text-gray-500 text-xs">
              Plateforme d'intelligence artificielle pour auto-entrepreneurs | Région Provence-Alpes-Côte d'Azur | 
              Nice • Cannes • Monaco • Marseille • Toulon
            </p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-700">
            {/* Modal Header */}
            <div className="bg-slate-900 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">
                {showModal === 'mentions' && 'Mentions Légales'}
                {showModal === 'privacy' && 'Politique de Confidentialité'}
                {showModal === 'cookies' && 'Politique des Cookies'}
                {showModal === 'cgu' && 'Conditions Générales d\'Utilisation'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] text-gray-300 text-sm space-y-4">
              {showModal === 'mentions' && <MentionsLegales />}
              {showModal === 'privacy' && <PolitiqueConfidentialite />}
              {showModal === 'cookies' && <PolitiqueCookies />}
              {showModal === 'cgu' && <CGU />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Mentions Légales
const MentionsLegales = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-white">Éditeur du site</h3>
    <p>
      <strong>Raison sociale :</strong> EvoCore PACA<br />
      <strong>Forme juridique :</strong> [À compléter]<br />
      <strong>Capital social :</strong> [À compléter]<br />
      <strong>Siège social :</strong> [Adresse à compléter], France<br />
      <strong>SIRET :</strong> [À compléter]<br />
      <strong>Email :</strong> contact@evocore-paca.fr<br />
      <strong>Téléphone :</strong> +33 X XX XX XX XX
    </p>

    <h3 className="text-lg font-semibold text-white">Directeur de la publication</h3>
    <p>[Nom du directeur de publication]</p>

    <h3 className="text-lg font-semibold text-white">Hébergement</h3>
    <p>
      <strong>Hébergeur :</strong> [Nom de l'hébergeur]<br />
      <strong>Adresse :</strong> [Adresse de l'hébergeur]<br />
      <strong>Téléphone :</strong> [Téléphone de l'hébergeur]
    </p>

    <h3 className="text-lg font-semibold text-white">Propriété intellectuelle</h3>
    <p>
      L'ensemble du contenu de ce site (textes, images, vidéos, etc.) est protégé par le droit d'auteur. 
      Toute reproduction, même partielle, est interdite sans autorisation préalable.
    </p>

    <h3 className="text-lg font-semibold text-white">Crédits</h3>
    <p>
      Design et développement : EvoCore PACA<br />
      Icônes : Lucide Icons<br />
      Photographies : [Sources à compléter]
    </p>
  </div>
);

// Politique de Confidentialité
const PolitiqueConfidentialite = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-white">1. Collecte des données</h3>
    <p>
      Nous collectons les données personnelles suivantes lorsque vous utilisez notre site :
    </p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li>Nom et prénom</li>
      <li>Adresse email</li>
      <li>Entreprise (optionnel)</li>
      <li>Messages et demandes de contact</li>
      <li>Données de navigation (cookies)</li>
    </ul>

    <h3 className="text-lg font-semibold text-white">2. Utilisation des données</h3>
    <p>Vos données sont utilisées pour :</p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li>Répondre à vos demandes de contact</li>
      <li>Améliorer nos services</li>
      <li>Vous envoyer des informations sur nos activités (avec votre consentement)</li>
      <li>Respecter nos obligations légales</li>
    </ul>

    <h3 className="text-lg font-semibold text-white">3. Conservation des données</h3>
    <p>
      Vos données personnelles sont conservées pendant une durée maximale de 3 ans à compter de votre dernier contact avec nous.
    </p>

    <h3 className="text-lg font-semibold text-white">4. Vos droits (RGPD)</h3>
    <p>Conformément au RGPD, vous disposez des droits suivants :</p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données</li>
      <li><strong>Droit de rectification :</strong> Corriger vos données inexactes</li>
      <li><strong>Droit à l'effacement :</strong> Supprimer vos données</li>
      <li><strong>Droit à la portabilité :</strong> Récupérer vos données dans un format structuré</li>
      <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
    </ul>
    <p className="mt-2">
      Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@evocore-paca.fr" className="text-blue-400 hover:underline">contact@evocore-paca.fr</a>
    </p>

    <h3 className="text-lg font-semibold text-white">5. Sécurité</h3>
    <p>
      Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre 
      tout accès non autorisé, perte ou destruction.
    </p>

    <h3 className="text-lg font-semibold text-white">6. Partage des données</h3>
    <p>
      Vos données ne sont jamais vendues à des tiers. Elles peuvent être partagées uniquement avec nos prestataires 
      techniques (hébergement, email) dans le cadre strict de nos services.
    </p>

    <h3 className="text-lg font-semibold text-white">7. Contact DPO</h3>
    <p>
      Pour toute question concernant vos données personnelles :<br />
      Email : <a href="mailto:dpo@evocore-paca.fr" className="text-blue-400 hover:underline">dpo@evocore-paca.fr</a>
    </p>
  </div>
);

// Politique des Cookies
const PolitiqueCookies = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-white">Qu'est-ce qu'un cookie ?</h3>
    <p>
      Un cookie est un petit fichier texte déposé sur votre ordinateur lors de la visite d'un site web. 
      Il permet de mémoriser des informations sur votre navigation.
    </p>

    <h3 className="text-lg font-semibold text-white">Cookies utilisés sur ce site</h3>
    
    <h4 className="font-semibold text-white">1. Cookies essentiels</h4>
    <p>Ces cookies sont nécessaires au fonctionnement du site :</p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li><strong>Session :</strong> Maintient votre session de navigation</li>
      <li><strong>Authentification :</strong> Mémorise votre connexion admin</li>
    </ul>

    <h4 className="font-semibold text-white">2. Cookies de performance</h4>
    <p>Ces cookies nous aident à améliorer le site :</p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li><strong>Analytics :</strong> Mesure l'audience et les performances</li>
      <li><strong>Préférences :</strong> Mémorise vos choix (langue, thème)</li>
    </ul>

    <h3 className="text-lg font-semibold text-white">Gestion des cookies</h3>
    <p>Vous pouvez gérer les cookies de plusieurs façons :</p>
    
    <h4 className="font-semibold text-white">Dans votre navigateur</h4>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li><strong>Chrome :</strong> Paramètres → Confidentialité et sécurité → Cookies</li>
      <li><strong>Firefox :</strong> Options → Vie privée et sécurité → Cookies</li>
      <li><strong>Safari :</strong> Préférences → Confidentialité → Cookies</li>
      <li><strong>Edge :</strong> Paramètres → Cookies et autorisations de site</li>
    </ul>

    <h4 className="font-semibold text-white mt-4">Refuser les cookies</h4>
    <p>
      Vous pouvez refuser les cookies non essentiels. Cependant, certaines fonctionnalités du site 
      pourraient ne plus fonctionner correctement.
    </p>

    <h3 className="text-lg font-semibold text-white">Durée de conservation</h3>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li><strong>Cookies de session :</strong> Supprimés à la fermeture du navigateur</li>
      <li><strong>Cookies persistants :</strong> Maximum 13 mois</li>
    </ul>

    <h3 className="text-lg font-semibold text-white">Cookies tiers</h3>
    <p>
      Nous n'utilisons pas de cookies publicitaires tiers. Les seuls cookies tiers éventuels 
      proviennent de services essentiels (hébergement, analytics).
    </p>
  </div>
);

// CGU
const CGU = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-white">1. Objet</h3>
    <p>
      Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du site EvoCore PACA 
      et de ses services.
    </p>

    <h3 className="text-lg font-semibold text-white">2. Acceptation des CGU</h3>
    <p>
      L'utilisation du site implique l'acceptation pleine et entière des présentes CGU. 
      Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le site.
    </p>

    <h3 className="text-lg font-semibold text-white">3. Services proposés</h3>
    <p>EvoCore PACA propose les services suivants :</p>
    <ul className="list-disc list-inside space-y-1 ml-4">
      <li>Plateforme d'information sur nos solutions IA</li>
      <li>Formulaires de contact et demandes d'information</li>
      <li>Accès à des ressources et documents</li>
      <li>Espace administrateur sécurisé</li>
    </ul>

    <h3 className="text-lg font-semibold text-white">4. Accès au site</h3>
    <p>
      L'accès au site est gratuit. Les frais de connexion et d'équipement restent à la charge de l'utilisateur.
      Nous nous réservons le droit de suspendre ou interrompre l'accès au site pour maintenance.
    </p>

    <h3 className="text-lg font-semibold text-white">5. Propriété intellectuelle</h3>
    <p>
      Tous les contenus du site (textes, images, logos, vidéos) sont protégés par le droit d'auteur. 
      Toute reproduction sans autorisation est interdite.
    </p>

    <h3 className="text-lg font-semibold text-white">6. Responsabilité</h3>
    <p>
      EvoCore PACA s'efforce d'assurer l'exactitude des informations diffusées mais ne peut garantir 
      l'absence d'erreurs. L'utilisateur utilise le site à ses propres risques.
    </p>

    <h3 className="text-lg font-semibold text-white">7. Données personnelles</h3>
    <p>
      Le traitement de vos données personnelles est régi par notre Politique de Confidentialité, 
      conforme au RGPD.
    </p>

    <h3 className="text-lg font-semibold text-white">8. Liens externes</h3>
    <p>
      Le site peut contenir des liens vers des sites externes. Nous ne sommes pas responsables 
      du contenu de ces sites tiers.
    </p>

    <h3 className="text-lg font-semibold text-white">9. Modification des CGU</h3>
    <p>
      Nous nous réservons le droit de modifier les présentes CGU à tout moment. 
      Les modifications prennent effet dès leur publication sur le site.
    </p>

    <h3 className="text-lg font-semibold text-white">10. Droit applicable</h3>
    <p>
      Les présentes CGU sont régies par le droit français. Tout litige sera soumis aux tribunaux compétents.
    </p>

    <h3 className="text-lg font-semibold text-white">11. Contact</h3>
    <p>
      Pour toute question concernant ces CGU :<br />
      Email : <a href="mailto:contact@evocore-paca.fr" className="text-blue-400 hover:underline">contact@evocore-paca.fr</a>
    </p>

    <p className="text-xs text-gray-500 mt-6">
      Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
    </p>
  </div>
);

export default Footer;
